(ns practitest.frontend.dashboard-item-settings.views
  (:require [re-frame.core :as re-frame]
            [reagent.core :as reagent]
            [clojure.string :as str]
            [practitest.frontend.common.i18n :refer [get-translation]]
            [practitest.frontend.components.selector :as selector]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components2.buttons :as buttons]
            [practitest.frontend.common.tooltip :as common-tooltip]
            [practitest.frontend.components2.spinners :as spinners]
            [practitest.frontend.components2.dashboard.chart :as dashboard-chart]
            [practitest.frontend.components.project-settings-tabs :as project-settings-tabs]
            [practitest.frontend.pt-ts-bridge :as pt-ts]
            [practitest.frontend.routing.backend-routes :as broutes]
            [practitest.frontend.dashboard-item-settings.events :as events]
            [practitest.frontend.dashboard-item-settings.subs :as subs]
            [practitest.frontend.subs :as global-subs]))

(def entity-options
  [{:value "Issue"       :label "Issue"}
   {:value "QtestDesign" :label "Test"}
   {:value "Suite"       :label "Test Set"}
   {:value "Instance"    :label "Instance"}
   {:value "Requirement" :label "Requirement"}
   {:value "QtestRun"    :label "Run"}])

(def entity->module
  {"Issue"       "Issue"
   "QtestDesign" "Test"
   "Suite"       "TestSet"
   "Instance"    "TestSet"
   "Requirement" "Requirement"
   "QtestRun"    "TestSet"})

(defn- item-types-for-entity [item-types entity]
  (filter #(some #{entity} (:entities %)) item-types))

;; Mirror old dashboard_items.js: for Issue entity, "Execution Progress" is displayed as
;; "Status Aggregated Progress" to match old UI behavior.
(defn- apply-entity-label-overrides [item-types entity]
  (if (= entity "Issue")
    (map #(if (= (:key %) "execution_stats")
            (assoc % :label "Status Aggregated Progress")
            %)
         item-types)
    item-types))

(defn- form-row [label required? & children]
  [:div.dashboard-item-form-row
   [:label.dashboard-item-form-label
    label
    (when required? [:span.required-mark " *"])]
   (into [:div.dashboard-item-form-control] children)])

(defn- select-field
  "`on-change` receives the selected option's `option-key` value; defaults to updating `field`."
  [item {:keys [label field items option-key label-fn placeholder required? disabled? on-change]}]
  (let [on-change (or on-change #(re-frame/dispatch [::events/update-field field %]))]
    [form-row label required?
     [selector/new-select
      {:items       items
       :key-fn      option-key
       :label-fn    label-fn
       :value       (some #(when (= (option-key %) (get item field)) %) items)
       :placeholder placeholder
       :disabled?   disabled?
       :on-change   #(on-change (option-key %))}]]))

(defn- name-field [item]
  [form-row (get-translation "dashboard-item.form.name" "Name") false
   [:input.form-control
    {:type        "text"
     :value       (or (:name item) "")
     :placeholder (get-translation "dashboard-item.form.name-placeholder" "Leave empty to use the default name.")
     :on-change   #(re-frame/dispatch [::events/update-field :name (.. % -target -value)])}]])

(defn- custom-view-field [item project-id]
  (let [entity    (:entity item)
        module    (get entity->module entity)
        view-id   (:custom_view_id item)
        user-id   (:id @(re-frame/subscribe [::global-subs/current-user]))
        on-change (fn [node]
                    (let [js-obj       (js->clj node :keywordize-keys true)
                          raw-id       (str (:id js-obj))
                          id-part      (first (str/split raw-id #"/"))
                          clear?       (= id-part "_DEFAULT_")
                          auto-filters (when-not clear?
                                         (filter #(not (str/blank? %)) (or (:auto_filter js-obj) [])))
                          auto-filter  (when (seq auto-filters) (str/join ";" auto-filters))]
                      (re-frame/dispatch [::events/update-custom-view
                                         (when-not clear? id-part)
                                         auto-filter])))
        label     (if (contains? #{"Instance" "QtestRun"} entity)
                    (get-translation "dashboard-item.form.test-set-filter" "Test Set level filter")
                    (get-translation "dashboard-item.form.custom-view" "Filter"))]
    [form-row label false
     [:div.dashboard-item-filter-tree {:key module}
      (when module
        [:> pt-ts/GenericSearchableTree
         {:urls                (clj->js
                                {:fetchRootTreeUrl (broutes/get-url "custom_views#by_entity"
                                                                    {:p_id project-id}
                                                                    {:entity_name module})
                                 :fetchTreeNode    (broutes/get-url "custom_views#fetch_filter"
                                                                    {:p_id project-id}
                                                                    {:entity_name module})
                                 :search           (broutes/get-url "custom_views#search"
                                                                    {:p_id project-id}
                                                                    {:entity_name module})})
          :scope               (clj->js {:userId    (str user-id)
                                         :projectId (str project-id)
                                         :module    module
                                         :entity    entity
                                         :tab       "filters"})
          :customClassName     "dashboard-item-searchable-tree"
          :selectedValue       (when view-id (str view-id))
          :initialSelectedItem (clj->js {:id   (if view-id (str view-id) "")
                                         :name (if view-id (or (:custom_view_name item) "") "")})
          :onChange            on-change}])]]))

(defn- custom-view-placeholder []
  [form-row (get-translation "dashboard-item.form.custom-view" "Filter") false
   [:div.dashboard-item-filter-tree-placeholder
    (get-translation "dashboard-item.form.select-entity-first" "Select an entity first")]])

(defn- graph-date-range-field [item]
  (let [item-type  (:item_type item)
        definition (or (:definition item) {})]
    (cond
      (= item-type "execution_stats")
      [form-row (get-translation "dashboard-item.form.graph-date-range" "Graph's Date range") false
       [:div.dashboard-item-date-range
        [:input.form-control.dashboard-item-date-range-gaps
         {:type      "number"
          :min       1
          :max       12
          :value     (or (:gaps definition) 1)
          :on-change #(re-frame/dispatch [::events/update-definition :gaps (.. % -target -value)])}]
        [selector/new-select
         {:items       [{:value "weeks"  :label (get-translation "generic.weeks" "weeks")}
                        {:value "months" :label (get-translation "generic.months" "months")}]
          :key-fn      :value
          :label-fn    :label
          :value       (let [v (or (:weeks_or_months definition) "months")]
                         {:value v :label (get-translation (str "generic." v) v)})
          :on-change   #(re-frame/dispatch [::events/update-definition :weeks_or_months (:value %)])}]
        [:span.dashboard-item-date-range-hint
         (get-translation "dashboard-item.form.max-interval-3-months" "*Max interval is 3 months")]]]

      (= item-type "stacked_bar")
      [form-row (get-translation "dashboard-item.form.graph-date-range" "Graph's Date range") false
       [selector/new-select
        {:items       [{:value 1 :label "1 Week"}
                       {:value 2 :label "2 Weeks"}
                       {:value 4 :label "4 Weeks"}]
         :key-fn      :value
         :label-fn    :label
         :value       (let [v (or (:weeks_ago definition) 1)]
                        {:value v :label (str v (if (= v 1) " Week" " Weeks"))})
         :on-change   #(re-frame/dispatch [::events/update-definition :weeks_ago (:value %)])}]])))

(defn- entity-field-select [item entity-fields opts]
  [select-field item
   (merge {:items       entity-fields
           :option-key  :value
           :label-fn    :label
           :placeholder (get-translation "dashboard-item.form.select-field" "Select field...")}
          opts)])

(defn- run-field-filter-field [item entity-fields field-pvs]
  (let [selected-field (some #(when (= (:value %) (:run_field_filter_id item)) %) entity-fields)
        criteria-opts  (mapv #(hash-map :value % :label %)
                             (get field-pvs (keyword (:run_field_filter_id item)) []))
        selected-crit  (some #(when (= (:value %) (:run_field_filter_criteria item)) %) criteria-opts)
        none-opt       {:value nil :label (get-translation "generic.none" "None")}]
    [:div.dashboard-item-run-filter-row
     [selector/new-select
      {:items       (into [none-opt] entity-fields)
       :key-fn      :value
       :label-fn    :label
       :value       (or selected-field none-opt)
       :on-change   #(re-frame/dispatch [::events/update-run-field-filter (:value %)])}]
     (when (:run_field_filter_id item)
       [:<>
        [:span.dashboard-item-run-filter-is (get-translation "dashboard-item.form.is" "Is")]
        [selector/new-select
         {:items       criteria-opts
          :key-fn      :value
          :label-fn    :label
          :value       selected-crit
          :placeholder (get-translation "dashboard-item.form.select-value" "Select value...")
          :on-change   #(re-frame/dispatch [::events/update-field :run_field_filter_criteria (:value %)])}]])]))

(defn- instance-filter-list-values [filter-val val-opts str-vals update!]
  [:div.dashboard-item-instance-values
   (doall
    (map-indexed
     (fn [idx v]
       (let [selected (some #(when (= (:value %) v) %) val-opts)]
         [:div.dashboard-item-instance-or-row {:key idx}
          [selector/new-select
           {:items       val-opts
            :key-fn      :value
            :label-fn    :label
            :value       selected
            :placeholder (get-translation "dashboard-item.form.select-value" "Select value...")
            :on-change   #(update! (assoc filter-val :string_values
                                          (assoc (vec str-vals) idx (:value %))))}]
          (when (> (count str-vals) 1)
            [buttons/icon-button
             {:icon     [icons/mux-x]
              :on-click #(update! (assoc filter-val :string_values
                                         (vec (concat (subvec (vec str-vals) 0 idx)
                                                      (subvec (vec str-vals) (inc idx))))))}])]))
     str-vals))
   [buttons/button
    {:text      (str "+ " (get-translation "view-editor.basic.or" "OR"))
     :tertiary? true
     :on-click  #(update! (assoc filter-val :string_values (conj (vec str-vals) nil)))}]])

(defn- instance-filter-field [item instance-fields]
  (let [filter-val   (or (get-in item [:definition :instance_filter]) {})
        field-id     (:field filter-val)
        operator     (:operator filter-val)
        field-meta   (some #(when (= (:value %) field-id) %) instance-fields)
        field-format (:field_format field-meta)
        date-field?  (= field-format "date")
        list-field?  (contains? #{"list" "multilist"} field-format)
        none-opt     {:value nil :label (get-translation "generic.none" "None")}
        field-opts   (into [none-opt] instance-fields)
        selected-fld (some #(when (= (:value %) field-id) %) field-opts)
        list-ops     [{:value 0  :label (get-translation "dashboard-item.form.is" "Is")}
                      {:value 1  :label (get-translation "dashboard-item.form.is-not" "Is Not")}]
        date-ops     [{:value 8  :label (get-translation "dashboard-item.form.in-the-last" "In the last")}
                      {:value 11 :label (get-translation "dashboard-item.form.not-in-the-last" "Not in the last")}]
        ops          (cond list-field? list-ops date-field? date-ops :else [])
        selected-op  (some #(when (= (:value %) operator) %) ops)
        val-opts     (mapv #(hash-map :value % :label %) (:values field-meta))
        str-vals     (let [v (or (:string_values filter-val) (:values filter-val))] (if (seq v) v [nil]))
        date-vals    (or (:date_values filter-val) {})
        update!      #(re-frame/dispatch [::events/update-instance-filter %])]
    [form-row (get-translation "dashboard-item.form.instance-level-filter" "Instance level filter") false
     [:div.dashboard-item-instance-filter
      [selector/new-select
       {:items       field-opts
        :key-fn      :value
        :label-fn    :label
        :value       (or selected-fld none-opt)
        :on-change   #(let [v (:value %)]
                        (update! (if v {:field v} {})))}]
      (when (and field-id (or list-field? date-field?))
        [selector/new-select
         {:items       ops
          :key-fn      :value
          :label-fn    :label
          :value       selected-op
          :placeholder (get-translation "dashboard-item.form.select-value" "Select value...")
          :on-change   #(update! (assoc filter-val :operator (:value %)))}])
      (when (and field-id (some? operator) list-field?)
        [instance-filter-list-values filter-val val-opts str-vals update!])
      (when (and field-id (some? operator) date-field?)
        [:div.dashboard-item-date-filter
         [:input.form-control
          {:type      "number" :min 1 :max 100
           :value     (or (:numeric date-vals) "")
           :on-change #(update! (assoc filter-val :date_values (assoc date-vals :numeric (.. % -target -value))))}]
         [selector/new-select
          {:items       [{:value "days"   :label (get-translation "dashboard-item.form.days" "Days")}
                         {:value "weeks"  :label (get-translation "dashboard-item.form.weeks" "Weeks")}
                         {:value "months" :label (get-translation "dashboard-item.form.months" "Months")}]
           :key-fn      :value
           :label-fn    :label
           :value       (let [v (or (:text date-vals) "days")
                              label-key (str "dashboard-item.form." v)]
                          {:value v :label (get-translation label-key (str/capitalize v))})
           :on-change   #(update! (assoc filter-val :date_values (assoc date-vals :text (:value %))))}]])]]))

(defn- relative-day-opts []
  (let [days (get-translation "dashboard-item.form.days" "Days")]
    (mapv (fn [v] {:value v :label (str v " " days)})
          ["0" "7" "30" "90" "180"])))

(defn- planned-vs-executed-field [item]
  (let [definition   (or (:definition item) {})
        use-absolute (= (:use_date_range definition) "on")
        days-ago     (str (or (:days_ago definition) "30"))
        days-after   (str (or (:days_after definition) "0"))
        from-date    (or (:from_date definition) "")
        to-date      (or (:to_date definition) "")]
    [:<>
     [form-row (get-translation "dashboard-item.form.graph-date-range" "Graph's Date range") false
      [:div.dashboard-item-date-range-radio
       [:label
        [:input {:type      "radio"
                 :checked   (not use-absolute)
                 :on-change #(re-frame/dispatch [::events/update-definition :use_date_range nil])}]
        " " (get-translation "dashboard-item.form.relative" "Relative")]
       [:label
        [:input {:type      "radio"
                 :checked   use-absolute
                 :on-change #(re-frame/dispatch [::events/update-definition :use_date_range "on"])}]
        " " (get-translation "dashboard-item.form.absolute" "Absolute")]]]
     (if use-absolute
       [:<>
        [form-row (get-translation "dashboard-item.form.from-date" "From") false
         [:input.form-control
          {:type      "date"
           :value     from-date
           :on-change #(re-frame/dispatch [::events/update-definition :from_date (.. % -target -value)])}]]
        [form-row (get-translation "dashboard-item.form.to-date" "To") false
         [:input.form-control
          {:type      "date"
           :value     to-date
           :on-change #(re-frame/dispatch [::events/update-definition :to_date (.. % -target -value)])}]]]
       [:<>
        [form-row (get-translation "dashboard-item.form.past" "Past") false
         [selector/new-select
          {:items     (relative-day-opts)
           :key-fn    :value
           :label-fn  :label
           :value     (some #(when (= (:value %) days-ago) %) (relative-day-opts))
           :on-change #(re-frame/dispatch [::events/update-definition :days_ago (:value %)])}]]
        [form-row (get-translation "dashboard-item.form.next" "Next") false
         [selector/new-select
          {:items     (relative-day-opts)
           :key-fn    :value
           :label-fn  :label
           :value     (some #(when (= (:value %) days-after) %) (relative-day-opts))
           :on-change #(re-frame/dispatch [::events/update-definition :days_after (:value %)])}]]
        [:span.dashboard-item-date-range-hint
         (get-translation "dashboard-item.form.planned-vs-executed-max-days" "The graph will calculate no more than 180 days")]])
     [:div.dashboard-item-planned-note
      (get-translation "dashboard-item.form.planned-vs-executed-note"
                       "*\"Planned\" line will show only Instances that have a Planned Execution date either on the Instance itself or in the parent Test Set.")]]))

(defn- gauge-config-field [item]
  (let [definition (or (:definition item) {})
        first-val  (str (or (get definition "first-val") (get definition :first-val) "30"))
        second-val (str (or (get definition "second-val") (get definition :second-val) "70"))
        rows       [{:color-class "red"
                     :color       (get-translation "dashboard-item.form.gauge-red" "Red")
                     :status      (get-translation "dashboard-item.form.gauge-off-track" "Off Track")
                     :from        "0"
                     :to          first-val
                     :field       "first-val"}
                    {:color-class "yellow"
                     :color       (get-translation "dashboard-item.form.gauge-yellow" "Yellow")
                     :status      (get-translation "dashboard-item.form.gauge-at-risk" "At Risk")
                     :from        first-val
                     :to          second-val
                     :field       "second-val"}
                    {:color-class "green"
                     :color       (get-translation "dashboard-item.form.gauge-green" "Green")
                     :status      (get-translation "dashboard-item.form.gauge-on-track" "On Track")
                     :from        second-val
                     :to          "100"}]]
    [form-row (get-translation "dashboard-item.form.gauge-settings" "Gauge Settings") false
     [:div.dashboard-item-gauge-table
      [:span.dashboard-item-gauge-header (get-translation "dashboard-item.form.gauge-color" "Color")]
      [:span.dashboard-item-gauge-header (get-translation "dashboard-item.form.gauge-up-to" "Up to (%)")]
      [:span.dashboard-item-gauge-header (get-translation "dashboard-item.form.gauge-covers" "Covers")]
      (for [{:keys [color-class color status from to field]} rows]
        ^{:key color-class}
        [:<>
         [:div.dashboard-item-gauge-color
          [:span.dashboard-item-gauge-swatch {:class color-class}]
          [:span.dashboard-item-gauge-color-name color]
          [:span.dashboard-item-gauge-status (str "| " status)]]
         [:input.form-control
          {:type      "number" :min 0 :max 100
           :value     to
           :disabled  (nil? field)
           :on-change #(re-frame/dispatch [::events/update-definition field (.. % -target -value)])}]
         [:span.dashboard-item-gauge-covers (str from "% – " to "%")]])]]))

(defn- slice-colors-field [item]
  (let [colors (:colors item)]
    [:div.dashboard-item-form-row
     [:label.dashboard-item-form-label
      (get-translation "dashboard-item.form.slice-colors" "Slice Colors")]
     [:div.dashboard-item-form-control
      [:input.form-control
       {:type        "text"
        :value       (or colors "")
        :placeholder "new: '#FF0000', open: '#00DD00'"
        :on-change   #(re-frame/dispatch [::events/update-field :colors (.. % -target -value)])}]]
     [:p.dashboard-item-settings-footer-hint
      (get-translation "dashboard-item.form.slice-colors-hint"
                       "name: '#RGB' pairs separated by commas. Leave blank for defaults.")
      " ("
      [:a {:href "https://www.w3schools.com/html/html_colors.asp" :target "_blank"}
       (get-translation "dashboard-item.form.slice-colors-examples" "Click here for examples")]
      ")"]]))

(defn- advanced-settings [item advanced-options]
  (reagent/with-let [open? (reagent/atom false)]
    (let [adv?            (:advanced_option advanced-options)
          percentage?     (:percentage_support advanced-options)
          colors?         (:colors_customization advanced-options)
          width?          (:width_customization advanced-options)
          height?         (:height_customization advanced-options)
          remove-empty?   (:remove_empty_values_customization advanced-options)
          gauge?          (:gauge advanced-options)]
      [:<>
       [:div {:class    (str "dashboard-item-advanced-header" (when-not adv? " disabled"))
              :on-click #(when adv? (swap! open? not))}
        (if @open? [icons/mux-chevron-down2] [icons/mux-chevron-right])
        [:span (get-translation "dashboard-item.form.advanced-settings" "Advanced Settings")]]
       (when (and adv? @open?)
         [:div.dashboard-item-advanced-content
          (when percentage?
            [:div.m-checkbox-container
             [:label.m-checkbox-label
              [:input.m-checkbox
               {:type      "checkbox"
                :checked   (= "1" (:percentage_mode item))
                :on-change #(re-frame/dispatch [::events/update-field :percentage_mode
                                                (if (.. % -target -checked) "1" "0")])}]
              (get-translation "dashboard-item.form.percentage-mode" "Percentage Mode - Display Data with %")]])
          (when remove-empty?
            [:div.m-checkbox-container
             [:label.m-checkbox-label
              [:input.m-checkbox
               {:type      "checkbox"
                :checked   (not= "0" (:remove_empty_values item))
                :on-change #(re-frame/dispatch [::events/update-field :remove_empty_values
                                                (if (.. % -target -checked) "1" "0")])}]
              (get-translation "dashboard-item.form.remove-empty-values" "Remove Empty Values")]])
          (when width?
            [:div.dashboard-item-advanced-inline-row
             [:label.dashboard-item-form-label
              (get-translation "dashboard-item.form.item-width" "Item Width")]
             [:div.dashboard-item-advanced-radios
              (doall
               (for [w ["regular" "double"]]
                 [:label.dashboard-item-advanced-radio {:key w}
                  [:input {:type      "radio"
                           :checked   (= w (or (:cell_width item) "regular"))
                           :on-change #(re-frame/dispatch [::events/update-field :cell_width w])}]
                  " " (get-translation (str "dashboard-item.form." w) (str/capitalize w))]))]])
          (when height?
            [:div.dashboard-item-advanced-inline-row
             [:label.dashboard-item-form-label
              (get-translation "dashboard-item.form.item-height" "Item Height")]
             [:div.dashboard-item-advanced-radios
              (doall
               (for [h ["regular" "double"]]
                 [:label.dashboard-item-advanced-radio {:key h}
                  [:input {:type      "radio"
                           :checked   (= h (or (:cell_height item) "regular"))
                           :on-change #(re-frame/dispatch [::events/update-field :cell_height h])}]
                  " " (get-translation (str "dashboard-item.form." h) (str/capitalize h))]))]])
          (when colors?
            [slice-colors-field item])
          (when gauge?
            [gauge-config-field item])])])))

(defn- left-panel [item dashboards item-types entity-fields relevant-fields field-pvs instance-fields project-id advanced-options]
  (let [entity (:entity item)
        shows? (fn [f] (events/relevant-field? relevant-fields f))]
    [:div.dashboard-item-settings-left-panel
     [:div.dashboard-item-settings-card
      [:div.dashboard-item-settings-card-header
       (get-translation "dashboard-item.form.card-title" "Set Dashboard Item")]
      [:div.dashboard-item-settings-card-body
       [select-field item
        {:label       (get-translation "dashboard-item.form.dashboard-tab" "Dashboard Tab")
         :field       :dashboard_id
         :items       dashboards
         :option-key  :id
         :label-fn    :name
         :placeholder (get-translation "dashboard-item.form.select-dashboard-tab" "Select dashboard tab...")
         :required?   true}]
       [name-field item]
       [select-field item
        {:label       (get-translation "dashboard-item.form.entity" "Entity")
         :field       :entity
         :items       entity-options
         :option-key  :value
         :label-fn    :label
         :placeholder (get-translation "dashboard-item.form.select-entity" "Select entity...")
         :required?   true
         :on-change   #(re-frame/dispatch [::events/update-entity %])}]
       [select-field item
        {:label       (get-translation "dashboard-item.form.item-type" "Item Type")
         :field       :item_type
         :items       (apply-entity-label-overrides (item-types-for-entity item-types entity) entity)
         :option-key  :key
         :label-fn    :label
         :placeholder (get-translation "dashboard-item.form.select-item-type" "Select item type...")
         :required?   true
         :disabled?   (nil? entity)
         :on-change   #(re-frame/dispatch [::events/update-item-type %])}]
       (if entity
         [custom-view-field item project-id]
         [custom-view-placeholder])
       (when (= entity "Instance")
         [instance-filter-field item instance-fields])
       (when (shows? "run_field_filter")
         [form-row (get-translation "dashboard-item.form.run-level-filter" "Run level filter") false
          [run-field-filter-field item entity-fields field-pvs]])
       (when (shows? "x_field")
         [entity-field-select item entity-fields
          {:label     (get-translation "dashboard-item.form.x-field" "Field")
           :field     :x_field_id
           :on-change #(re-frame/dispatch [::events/update-x-field %])}])
       (when (and (shows? "x_field_criteria") (:x_field_id item))
         [select-field item
          {:label       (get-translation "dashboard-item.form.x-field-criteria" "Field Value")
           :field       :x_field_criteria
           :items       (mapv (fn [v] {:value v :label v}) (get field-pvs (keyword (:x_field_id item)) []))
           :option-key  :value
           :label-fn    :label
           :placeholder (get-translation "dashboard-item.form.select-value" "Select value...")}])
       (when (shows? "y_field")
         [entity-field-select item entity-fields
          {:label (get-translation "dashboard-item.form.y-field" "Y Field")
           :field :y_field_id}])
       (when (shows? "group_by")
         [entity-field-select item entity-fields
          {:label (get-translation "dashboard-item.form.group-by" "Group By")
           :field :y_field_id}])
       (when (contains? #{"execution_stats" "stacked_bar"} (:item_type item))
         [graph-date-range-field item])
       (when (shows? "planned_vs_executed_definition")
         [planned-vs-executed-field item])
       [advanced-settings item advanced-options]]]]))

(defn- right-panel [_project-id _dashboard-id _saved-item-id _embed-code]
  (let [copied? (reagent/atom false)]
    (fn [project-id dashboard-id saved-item-id embed-code]
      [:div.dashboard-item-settings-right-panel
       [:div.dashboard-item-settings-card
        [:div.dashboard-item-settings-card-header
         [:span (get-translation "dashboard-item.preview.title" "Dashboard Preview")]
         (when dashboard-id
           [:a.dashboard-item-preview-link {:href (str "/p/" project-id "/summary?id=" dashboard-id)}
            (get-translation "dashboard-item.preview.view-in-dashboard" "View in Dashboard")])]
        [:div.dashboard-item-settings-preview-area
         (if saved-item-id
           [:div.dashboard-item-preview-chart
            [dashboard-chart/chart #:chart{:id saved-item-id}]]
           [:div.dashboard-item-preview-placeholder
            [icons/mux-empty-dashboard]
            [:strong.dashboard-item-preview-title
             (get-translation "dashboard-item.preview.no-preview" "No preview yet")]
            [:span.dashboard-item-preview-subtitle
             (get-translation "dashboard-item.preview.hint" "Choose an Entity, Item Type and Filter, then select Show Preview.")]])]
        [:div.dashboard-item-embed-section
         [:div.dashboard-item-embed-header
          [:span.dashboard-item-embed-label
           (get-translation "dashboard-item.embed.label" "Embed Code")
           [common-tooltip/info-circle-tooltip
            [:span
             (get-translation "dashboard-item.embed.info-note-prefix"
                              "To embed a dashboard item, the tab it belongs to needs to be ")
             [:a {:href   "https://www.practitest.com/help/reporting/external-dashboard-kitchen-monitor/"
                  :target "_blank"}
              (get-translation "dashboard-item.embed.externally-accessible" "Externally Accessible")]]
            {:trigger "click" :class "cursor-pointer"}]]
          [buttons/button
           {:icon      [icons/mux-copy]
            :text      (if @copied?
                         (get-translation "dashboard-item.embed.copied" "Copied!")
                         (get-translation "dashboard-item.embed.copy-code" "Copy Code"))
            :tertiary? true
            :disabled? (nil? embed-code)
            :on-click  #(do (js/navigator.clipboard.writeText embed-code)
                            (reset! copied? true)
                            (js/setTimeout (fn [] (reset! copied? false)) 1500))}]]
         [:textarea.form-control.dashboard-item-embed-code
          {:read-only   true
           ;; Saved but no embed code -> the item's tab doesn't allow external access
           :placeholder (if saved-item-id
                          (get-translation "dashboard-item.embed.placeholder-not-external"
                                           "To embed this item, allow external access to its dashboard tab (Project Settings > Dashboard > Edit Tab).")
                          (get-translation "dashboard-item.embed.placeholder" "Available after you save this item."))
           :value       (or embed-code "")}]]]])))

(defn- page-footer [saving?]
  [:div.dashboard-item-settings-footer
   [:span.dashboard-item-settings-footer-hint
    (get-translation "dashboard-item.footer.hint" "Save to publish this item to the dashboard.")]
   [:div.dashboard-item-settings-footer-actions
    [buttons/button {:text     (get-translation "generic.button.cancel" "Cancel")
                     :on-click #(re-frame/dispatch [::events/cancel])}]
    [buttons/button {:text      (get-translation "generic.button.save" "Save")
                     :disabled? saving?
                     :on-click  #(re-frame/dispatch [::events/save {:close? false}])}]
    [buttons/button {:text      (get-translation "generic.button.save-and-close" "Save and Close")
                     :primary?  true
                     :disabled? saving?
                     :on-click  #(re-frame/dispatch [::events/save {:close? true}])}]]])

(defn page [{:keys [edit?]}]
  (reagent/with-let [dirty-sub (re-frame/subscribe [::subs/field :dirty?])
                     on-unload (fn [e]
                                 (when @dirty-sub
                                   (.preventDefault e)
                                   (set! (.-returnValue e) "unsaved-changes")
                                   e))
                     _         (js/window.addEventListener "beforeunload" on-unload)]
    (let [item             @(re-frame/subscribe [::subs/field :item])
          dashboards       @(re-frame/subscribe [::subs/field :dashboards])
          item-types       @(re-frame/subscribe [::subs/field :item-types])
          loading?         @(re-frame/subscribe [::subs/field :loading?])
          saving?          @(re-frame/subscribe [::subs/field :saving?])
          saved-item-id    @(re-frame/subscribe [::subs/field :saved-item-id])
          embed-code       @(re-frame/subscribe [::subs/field :embed-code])
          entity-fields    @(re-frame/subscribe [::subs/field :entity-fields])
          relevant-fields  @(re-frame/subscribe [::subs/field :relevant-fields])
          field-pvs        @(re-frame/subscribe [::subs/field :field-pvs])
          instance-fields  @(re-frame/subscribe [::subs/field :instance-fields])
          advanced-options @(re-frame/subscribe [::subs/field :advanced-options])
          project-id       @(re-frame/subscribe [::subs/project-id])
          title            (if edit?
                             (get-translation "dashboard-item.edit.title" "Edit Dashboard Item")
                             (get-translation "dashboard-item.new.title" "New Dashboard Item"))
          breadcrumbs      [{:label title}]]
      [:div.dashboard-item-settings-page
       [:div.global-fields-sticky-header
        [project-settings-tabs/project-settings-tabs {:breadcrumbs breadcrumbs}]
        [:h1.dashboard-item-settings-title title]]
       [:div.dashboard-item-settings-body
        (if loading?
          [:div.dashboard-item-settings-loading [spinners/mux-spinner]]
          [:div.dashboard-item-settings-panels
           [left-panel item dashboards item-types entity-fields relevant-fields field-pvs instance-fields project-id advanced-options]
           [right-panel project-id (:dashboard_id item) saved-item-id embed-code]])]
       [page-footer saving?]])
    (finally
      (js/window.removeEventListener "beforeunload" on-unload))))
